import React, { forwardRef, RefObject, useContext, useRef } from 'react';
import classNames from 'classnames';
import { useMenuItem } from './useMenuItem';
import { useMergeRefs } from '@redsift/popovers';
import {
  ButtonsColorPalette,
  Comp,
  ConditionalWrapper,
  FocusWithinGroupContext,
  getContainerStylingTransientProps,
  isComponent,
  StyledGradientBorder,
  useTheme,
  RenderedListboxItem,
  ItemProps,
  StyledItem,
  useId,
} from '@redsift/design-system';

const COMPONENT_NAME = 'Item';
const CLASSNAME = 'redsift-item';

const RenderedMenuItem: Comp<Omit<ItemProps, 'value'>, HTMLElement> = forwardRef((props, ref) => {
  const { transientProps, otherProps } = getContainerStylingTransientProps(props);

  const {
    borderRadius = '0',
    children,
    className,
    color: propsColor,
    hasBorder,
    id: propsId,
    isActive,
    isDisabled: propsIsDisabled,
    isHovered,
    theme: propsTheme,
    onClick,
    ...forwardedProps
  } = otherProps;
  const [_id] = useId();
  const id = propsId ?? _id;
  const _ref = useRef<HTMLElement>();
  const divRef = useMergeRefs([ref, _ref]);
  const linkRef = useRef<HTMLAnchorElement>(null);

  const isLinkMenuItem = isComponent('Link')(children);
  const { tabIndex, isFocused, isDisabled, handleKeyDown, handleClick } = useMenuItem({
    domElementRef: _ref as RefObject<Element>,
    id,
    isDisabled: propsIsDisabled!,
    onClick: onClick as any,
    isLinkMenuItem,
    linkRef,
    hasPopup: props['aria-haspopup'] !== undefined,
  });

  const theme = useTheme(propsTheme);
  const color = Object.values(ButtonsColorPalette).includes(propsColor!) ? propsColor : 'primary';
  const isGradient = color === ButtonsColorPalette.radar;

  return (
    <ConditionalWrapper
      condition={isGradient}
      wrapper={(children) => (
        <StyledGradientBorder
          $borderRadius={borderRadius}
          $color={color}
          $isActive={isActive}
          $isDisabled={isDisabled}
          $isHovered={isHovered}
          $theme={theme}
          width="auto"
        >
          {children}
        </StyledGradientBorder>
      )}
    >
      <StyledItem
        as="li"
        role={isLinkMenuItem ? 'none' : 'menuitem'}
        {...forwardedProps}
        {...transientProps}
        id={id}
        $borderRadius={borderRadius}
        $color={color}
        $hasBorder={hasBorder}
        $hasCheckbox={false}
        $isActive={isActive}
        $isDisabled={isDisabled}
        $isGradient={isGradient}
        $isHovered={isHovered || isFocused}
        $theme={theme}
        aria-disabled={isDisabled}
        className={classNames(Item.className, className)}
        onClick={isDisabled ? undefined : handleClick}
        ref={divRef}
        onKeyDown={handleKeyDown}
        tabIndex={tabIndex}
      >
        {isLinkMenuItem
          ? React.cloneElement(children, {
              role: 'menuitem',
              ref: linkRef,
            })
          : children}
      </StyledItem>
    </ConditionalWrapper>
  );
});

/**
 * The Item component.
 */
export const Item: Comp<ItemProps, HTMLElement> = forwardRef((props, ref) => {
  const { value } = props;
  const focusContext = useContext(FocusWithinGroupContext);
  const isListbox = focusContext.state.listRole === 'listbox';

  if (!focusContext.state.filter) {
    return isListbox ? (
      <RenderedListboxItem {...props} value={props.value!} ref={ref} />
    ) : (
      <RenderedMenuItem {...props} ref={ref} />
    );
  }

  const { value: filterValue, type, caseSensitive } = focusContext.state.filter;

  if (!filterValue) {
    return isListbox ? (
      <RenderedListboxItem {...props} value={props.value!} ref={ref} />
    ) : (
      <RenderedMenuItem {...props} ref={ref} />
    );
  } else if (
    caseSensitive &&
    ((type === 'startsWith' && value!.startsWith(filterValue)) ||
      (type === 'contains' && value!.includes(filterValue)) ||
      (type === 'endsWith' && value!.endsWith(filterValue)))
  ) {
    return isListbox ? (
      <RenderedListboxItem {...props} value={props.value!} ref={ref} />
    ) : (
      <RenderedMenuItem {...props} ref={ref} />
    );
  } else if (
    !caseSensitive &&
    ((type === 'startsWith' && value!.toLowerCase().startsWith(filterValue.toLowerCase())) ||
      (type === 'contains' && value!.toLowerCase().includes(filterValue.toLowerCase())) ||
      (type === 'endsWith' && value!.toLowerCase().endsWith(filterValue.toLowerCase())))
  ) {
    return isListbox ? (
      <RenderedListboxItem {...props} value={props.value!} ref={ref} />
    ) : (
      <RenderedMenuItem {...props} ref={ref} />
    );
  }

  return null;
});
Item.className = CLASSNAME;
Item.displayName = COMPONENT_NAME;
